/* SiteContentPanel — admin CMS editor for any of the static pages. Backed by
 * /api/admin/site-content/{page} (GET/PUT) which stores overrides in MongoDB
 * site_content; defaults live in backend site_content_defaults.py.
 *
 * Pages: home, how_to_use, faq, pricing. Each page has its own SECTIONS schema
 * below. The page selector at the top of the panel switches between them; all
 * other UX (save/reset/section accordion) is shared.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowCounterClockwise, FloppyDisk, Eye } from "@phosphor-icons/react";
import SnippetPreview from "@/components/SnippetPreview";
import Media from "@/components/Media";
import { HOME_ICONS } from "@/lib/homeIcons";

// Schema-driven UI: declare which fields each section has and the input variant (text/textarea).

// Reusable SEO section — pinned at the top of every page's schema so admins
// always know where to change the <title>, <meta description> and keywords
// for the corresponding public route.
const SEO_SECTION = {
  key: "seo",
  label: "SEO — search-engine title, description & keywords",
  fields: [
    {
      k: "seo_title",
      label: "Page title (≤60 chars — appears in Google, browser tab, share cards)",
      helper: "Best practice: brand name first if you want to rank on your own name. Example: “Recipe Filer — Save family recipes, scan & print cookbooks”.",
    },
    {
      k: "seo_description",
      label: "Meta description (145–160 chars — Google shows this under the title)",
      textarea: true,
      helper: "Warm, benefit-led, one clear promise. Google may truncate over 160 chars.",
    },
    {
      k: "seo_keywords",
      label: "Keywords (comma-separated, 10–15 terms)",
      textarea: true,
      helper: "Google ignores this tag, but Bing, DuckDuckGo, Yandex and AI search engines (ChatGPT, Perplexity, Google AI Overviews) still read it. Include brand + long-tail variants.",
    },
  ],
};

const HOME_SECTIONS = [
  SEO_SECTION,
  {
    key: "hero",
    label: "Hero",
    fields: [
      { k: "overline", label: "Eyebrow / overline" },
      { k: "h1_line1", label: "H1 — line 1" },
      { k: "h1_line2_orange", label: "H1 — line 2 (orange highlight)" },
      { k: "h1_line3", label: "H1 — line 3" },
      { k: "body", label: "Sub-headline", textarea: true },
      { k: "_hero_photo", label: "Hero photo or video", type: "hero_photo" },
      { k: "cta_primary_label", label: "Button 1 label — Start free (use {FREE} for free-recipe count)" },
      { k: "cta_primary_href", label: "Button 1 link" },
      { k: "cta_try_label", label: "Button 2 label — Try it free" },
      { k: "cta_try_href", label: "Button 2 link" },
      { k: "cta_howto_label", label: "Button 3 label — How To Use" },
      { k: "cta_howto_href", label: "Button 3 link" },
      { k: "cta_secondary_label", label: "Button 4 label — Browse public cookbooks" },
      { k: "cta_secondary_href", label: "Button 4 link" },
      { k: "trust_1", label: "Trust badge 1" },
      { k: "trust_2", label: "Trust badge 2" },
      { k: "trust_3", label: "Trust badge 3" },
      { k: "badge_text", label: "Floating amber badge" },
    ],
  },
  {
    key: "three_ways",
    label: "Home page cards (Scan a card / Paste a link / Type one in / Import)",
    fields: [
      { k: "overline", label: "Eyebrow" },
      { k: "h2_line1", label: "H2 — line 1" },
      { k: "h2_line2", label: "H2 — line 2" },
      { k: "h2_line3_orange", label: "H2 — line 3 (orange)" },
      { k: "intro", label: "Intro paragraph", textarea: true },
      { k: "footer_text", label: "Italic footer line", textarea: true },
      { k: "ios_label", label: "iOS CTA prefix" },
      { k: "ios_link_text", label: "iOS CTA link label" },
      { k: "ios_link_href", label: "iOS CTA link" },
      { k: "ios_suffix", label: "iOS CTA suffix" },
    ],
    arrayKey: "paths",
    arrayLabel: "The cards themselves — add / reorder / remove (4 sit across on desktop)",
    arrayItemLabel: "card",
    arrayFields: [
      { k: "eyebrow", label: "Eyebrow (e.g. 'Past')" },
      { k: "title", label: "Card title (e.g. 'Scan a card')" },
      { k: "body", label: "Card body", textarea: true },
      { k: "cta", label: "CTA label" },
      { k: "icon", label: "Icon", type: "icon" },
      { k: "href", label: "Card link (e.g. /scan?mode=url or https://…)" },
    ],
  },
  {
    key: "how_it_works",
    label: "How it works",
    fields: [
      { k: "overline", label: "Eyebrow" },
      { k: "h2_line1", label: "H2 — line 1" },
      { k: "h2_line2", label: "H2 — line 2" },
      { k: "h2_line3", label: "H2 — line 3" },
    ],
    arrayKey: "steps",
    arrayLabel: "Four steps",
    arrayFields: [
      { k: "title", label: "Title" },
      { k: "body", label: "Body", textarea: true },
      { k: "icon", label: "Icon", type: "icon" },
      { k: "href", label: "Optional link — leave blank for a plain box (e.g. /shopping or https://…)" },
    ],
  },
  {
    key: "features",
    label: "Kitchen-safe features",
    fields: [
      { k: "overline", label: "Eyebrow" },
      { k: "h3_line1", label: "H3 — line 1" },
      { k: "h3_line2", label: "H3 — line 2" },
      { k: "body", label: "Body", textarea: true },
      { k: "cta_label", label: "CTA label" },
      { k: "cta_href", label: "CTA link — leave blank to open a sample recipe in Kitchen View" },
    ],
    arrayKey: "items",
    arrayLabel: "Feature tiles",
    arrayFields: [
      { k: "title", label: "Title" },
      { k: "body", label: "Body", textarea: true },
      { k: "icon", label: "Icon", type: "icon" },
      { k: "href", label: "Optional link — leave blank for a plain box (e.g. /shopping or https://…)" },
    ],
  },
  {
    key: "shopping_list",
    label: "Shopping list & pantry",
    fields: [
      { k: "overline", label: "Eyebrow" },
      { k: "h3_line1", label: "H3 — line 1" },
      { k: "h3_line2", label: "H3 — line 2 (orange highlight)" },
      { k: "body", label: "Body", textarea: true },
      { k: "cta_label", label: "CTA button label" },
      { k: "cta_href", label: "CTA button link" },
    ],
    arrayKey: "items",
    arrayLabel: "Feature tiles (icons cycle: list · basket · pin · Amazon · export)",
    arrayFields: [
      { k: "title", label: "Title" },
      { k: "body", label: "Body", textarea: true },
      { k: "icon", label: "Icon", type: "icon" },
      { k: "href", label: "Optional link — leave blank for a plain box (e.g. /shopping or https://…)" },
    ],
  },
  {
    key: "meal_planner",
    label: "Weekly meal planner",
    fields: [
      { k: "overline", label: "Eyebrow" },
      { k: "h3_line1", label: "H3 — line 1" },
      { k: "h3_line2", label: "H3 — line 2 (orange highlight)" },
      { k: "body", label: "Body", textarea: true },
      { k: "cta_label", label: "CTA button label" },
      { k: "cta_href", label: "CTA button link" },
    ],
    arrayKey: "items",
    arrayLabel: "Feature tiles (icons cycle: calendar · book · pencil · fork · share)",
    arrayFields: [
      { k: "title", label: "Title" },
      { k: "body", label: "Body", textarea: true },
      { k: "icon", label: "Icon", type: "icon" },
      { k: "href", label: "Optional link — leave blank for a plain box (e.g. /shopping or https://…)" },
    ],
  },
  {
    key: "directory",
    label: "Directory peek",
    fields: [
      { k: "overline", label: "Eyebrow" },
      { k: "h2", label: "H2" },
      { k: "link_label", label: '"Browse all" link label' },
      { k: "link_href", label: '"Browse all" link' },
      { k: "empty_message", label: "Empty-state message", textarea: true },
      { k: "empty_cta", label: "Empty-state CTA label" },
      { k: "empty_cta_href", label: "Empty-state CTA link" },
    ],
  },
  {
    key: "news",
    label: "News peek",
    fields: [
      { k: "overline", label: "Eyebrow" },
      { k: "h2", label: "H2" },
      { k: "link_label", label: '"All articles" button label' },
      { k: "link_href", label: '"All articles" button link' },
    ],
  },
  {
    key: "cta",
    label: "Final CTA (orange band)",
    fields: [
      { k: "h2_line1", label: "H2 — line 1" },
      { k: "h2_line2", label: "H2 — line 2" },
      { k: "body_paid", label: "Body when subscriptions are LIVE (use {FREE})", textarea: true },
      { k: "body_free", label: "Body when subscriptions are HIDDEN (use {FREE})", textarea: true },
      { k: "button_label", label: "Button label" },
      { k: "button_href", label: "Button link" },
    ],
  },
];

// Schemas for the other CMS-enabled pages
const HOWTOUSE_SECTIONS = [
  SEO_SECTION,
  {
    key: "_root",
    label: "Page heading",
    fields: [
      { k: "overline", label: "Eyebrow" },
      { k: "h1", label: "H1" },
      { k: "intro", label: "Intro paragraph", textarea: true },
    ],
  },
  {
    key: "_root2",
    label: "iOS share-shortcut card",
    fields: [
      { k: "ios_card_title", label: "Title" },
      { k: "ios_card_body", label: "Body", textarea: true },
      { k: "ios_cta_label", label: "CTA label" },
    ],
  },
  {
    key: "_root",
    label: "Final CTA",
    fields: [
      { k: "cta_h2", label: "H2" },
      { k: "cta_body", label: "Body (use {FREE})", textarea: true },
      { k: "cta_button_label", label: "Button label" },
    ],
  },
  {
    arrayKey: "steps",
    arrayLabel: "The 4 steps",
    arrayFields: [
      { k: "title", label: "Title" },
      { k: "body", label: "Body", textarea: true },
    ],
  },
  {
    label: "Sub-cards under Step 1 (Scan a card / Paste a link / Type one in)",
    arrayKey: "input_paths",
    arrayLabel: "Cards — add / reorder / remove (leave 3 or add a 4th)",
    arrayItemLabel: "sub-card",
    arrayFields: [
      { k: "icon_key", label: "Icon (camera | link | pencil | share) — DO NOT invent new keys" },
      { k: "label", label: "Card title (e.g. 'Scan a card')" },
      { k: "body", label: "Card body", textarea: true },
    ],
  },
];

const VOICE_SECTIONS = [
  SEO_SECTION,
  {
    key: "_root",
    label: "Page heading",
    fields: [
      { k: "overline", label: "Eyebrow" },
      { k: "h1", label: "H1" },
      { k: "intro", label: "Intro paragraph", textarea: true },
      { k: "speakable_summary", label: "Speakable summary (read aloud by voice assistants)", textarea: true },
      { k: "howto_title", label: "How-to heading" },
    ],
  },
  { arrayKey: "howto_steps", arrayLabel: "How-to steps", arrayFields: [{ k: "title", label: "Title" }, { k: "body", label: "Body", textarea: true }] },
  {
    arrayKey: "sections", arrayLabel: "Feature sections", arrayItemLabel: "section",
    arrayFields: [{ k: "title", label: "Title" }, { k: "body", label: "Body", textarea: true }, { k: "examples", label: "Example phrases (one per line)", textarea: true }],
  },
  { arrayKey: "faqs", arrayLabel: "Voice FAQs (FAQPage schema)", arrayItemLabel: "FAQ", arrayFields: [{ k: "q", label: "Question" }, { k: "a", label: "Answer", textarea: true }] },
  {
    key: "_root",
    label: "Final CTA",
    fields: [{ k: "cta_h2", label: "H2" }, { k: "cta_body", label: "Body (use {FREE})", textarea: true }, { k: "cta_button_label", label: "Button label" }],
  },
];

const FAQ_SECTIONS = [
  SEO_SECTION,
  {
    key: "_root",
    label: "Page heading",
    fields: [
      { k: "overline", label: "Eyebrow" },
      { k: "h1", label: "H1" },
      { k: "intro", label: "Intro paragraph", textarea: true },
    ],
  },
  {
    key: "_root",
    label: "Still stuck card",
    fields: [
      { k: "contact_label", label: "Label" },
      { k: "contact_body", label: "Body" },
      { k: "contact_email", label: "Email" },
    ],
  },
];

const PRICING_SECTIONS = [
  SEO_SECTION,
  {
    key: "_root",
    label: "Page heading & coming-soon",
    fields: [
      { k: "overline", label: "Eyebrow" },
      { k: "h1_line1", label: "H1 line 1" },
      { k: "h1_line2", label: "H1 line 2" },
      { k: "intro", label: "Intro (use {FREE})", textarea: true },
      { k: "gift_title", label: "Gift panel — heading" },
      { k: "gift_body", label: "Gift panel — body", textarea: true },
      { k: "gift_placeholder", label: "Gift code input placeholder" },
      { k: "gift_cta", label: "Gift redeem button label" },
    ],
    arrayKey: "plans",
    arrayLabel: "Subscription plan cards (Taster / 1mo / 3mo / 6mo / 12mo)",
    arrayItemLabel: "plan tier",
    arrayFields: [
      { k: "id", label: "Plan id (free / 1mo / 3mo / 6mo / annual — DO NOT change)" },
      { k: "name", label: "Plan name" },
      { k: "price", label: "Price (e.g. £5)" },
      { k: "period", label: "Period suffix (e.g. per year)" },
      { k: "sub", label: "Sub-label (small orange tagline)" },
      { k: "cta", label: "Button label" },
      { k: "featured", label: "Featured? type 'true' or 'false'" },
      { k: "perks", label: "Perks — one per line. Use {FREE} for free-recipe count", textarea: true },
    ],
  },
];

const SCAN_SECTIONS = [
  SEO_SECTION,
  {
    key: "_root",
    label: "Page heading",
    fields: [
      { k: "overline", label: "Eyebrow / overline" },
      { k: "h1_line1", label: "H1 — line 1 (e.g. 'Five ways')" },
      { k: "h1_line2", label: "H1 — line 2 (e.g. 'to add a recipe.')" },
      { k: "intro", label: "Intro paragraph under the H1", textarea: true },
    ],
    arrayKey: "cards",
    arrayLabel: "Entry-way cards (Camera / Upload / Link / Manual / Social share…)",
    arrayItemLabel: "card",
    arrayFields: [
      { k: "action_key", label: "Action (camera | upload | url | manual | social_share) — DO NOT invent new keys" },
      { k: "title", label: "Card title" },
      { k: "body", label: "Card body", textarea: true },
      { k: "cta", label: "Bottom CTA text (e.g. 'Tap to open camera →')" },
    ],
  },
];

const PAGE_REGISTRY = [
  { key: "home", label: "Home page", sections: HOME_SECTIONS, route: "/" },
  { key: "scan", label: "Scan / Add recipe page", sections: SCAN_SECTIONS, route: "/scan" },
  { key: "how_to_use", label: "How To Use", sections: HOWTOUSE_SECTIONS, route: "/how-to-use" },
  { key: "voice", label: "Voice", sections: VOICE_SECTIONS, route: "/voice" },
  { key: "faq", label: "FAQ", sections: FAQ_SECTIONS, route: "/faqs" },
  { key: "pricing", label: "Pricing", sections: PRICING_SECTIONS, route: "/pricing" },
  // ── SEO-only pages ──
  // Body content on these pages is rendered from live data (recipes,
  // users, cookbooks), so the admin panel exposes ONLY the SEO section.
  // Every one of them gets a matching `seo` block in
  // `site_content_defaults.py` so overrides merge cleanly.
  { key: "directory",      label: "Directory (SEO only)",       sections: [SEO_SECTION], route: "/directory" },
  { key: "historical",     label: "Historical (SEO only)",      sections: [SEO_SECTION], route: "/historical" },
  { key: "news",           label: "News (SEO only)",            sections: [SEO_SECTION], route: "/news" },
  { key: "leaderboard",    label: "Leaderboard (SEO only)",     sections: [SEO_SECTION], route: "/leaderboard" },
  { key: "shop",           label: "Shop (SEO only)",            sections: [SEO_SECTION], route: "/shop" },
  { key: "recipe_creator", label: "Recipe Creator (SEO only)",  sections: [SEO_SECTION], route: "/recipe-creator" },
  { key: "my_cookbooks",   label: "My Cookbooks (SEO only)",    sections: [SEO_SECTION], route: "/my-cookbooks" },
  { key: "shopping",       label: "Shopping list (SEO only)",   sections: [SEO_SECTION], route: "/shopping" },
  { key: "planner",        label: "Meal planner (SEO only)",    sections: [SEO_SECTION], route: "/planner" },
  { key: "dashboard",      label: "Dashboard (SEO only)",       sections: [SEO_SECTION], route: "/dashboard" },
];

export default function HomeContentPanel() {
  const [page, setPage] = useState("home");
  const pageMeta = PAGE_REGISTRY.find((p) => p.key === page) || PAGE_REGISTRY[0];
  const SECTIONS = pageMeta.sections;
  const [resolved, setResolved] = useState(null); // current merged content (editable copy)
  const [defaults, setDefaults] = useState(null); // for reset-to-default hints
  const [saving, setSaving] = useState(false);
  // Auto-expand ALL sections whenever the page tab changes. Users were
  // missing the "Home page 3 cards" editor because sections defaulted to
  // collapsed and only the first one was pre-opened. Opening everything
  // makes it obvious what's editable, and each section still has a header
  // click-to-collapse for tidying.
  const [openSections, setOpenSections] = useState(() => new Set());
  useEffect(() => {
    const all = new Set();
    SECTIONS.forEach((sec, sidx) => {
      const key = sec.key || `arr-${sec.arrayKey}` || `sec-${sidx}`;
      all.add(`${key}-${sidx}`);
    });
    setOpenSections(all);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/site-content/${page}`);
      setDefaults(data.defaults);
      setResolved(data.resolved);
    } catch {
      toast.error("Couldn't load page content");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- load() is a stable closure; re-fetch only when the page changes
  useEffect(() => { load(); }, [page]);

  const updateField = (section, key, value) => {
    setResolved((prev) => {
      // _root pseudo-section: write directly to the top-level resolved tree
      if (section === "_root" || section === "_root2") {
        return { ...prev, [key]: value };
      }
      return {
        ...prev,
        [section]: { ...(prev[section] || {}), [key]: value },
      };
    });
  };

  const updateArrayField = (section, arrKey, index, key, value) => {
    setResolved((prev) => {
      // When section is null or "_root", the array sits at the top level of `resolved`
      const isRoot = !section || section === "_root" || section === "_root2";
      const parent = isRoot ? prev : (prev[section] || {});
      const arr = [...(parent[arrKey] || [])];
      arr[index] = { ...arr[index], [key]: value };
      if (isRoot) {
        return { ...prev, [arrKey]: arr };
      }
      return { ...prev, [section]: { ...parent, [arrKey]: arr } };
    });
  };

  // Add a blank item at the end of an editable array. Each field declared in
  // `arrayFields` initialises to an empty string so the UI is immediately
  // editable.
  const addArrayItem = (section, arrKey, arrayFields) => {
    const blank = Object.fromEntries((arrayFields || []).map((f) => [f.k, ""]));
    setResolved((prev) => {
      const isRoot = !section || section === "_root" || section === "_root2";
      const parent = isRoot ? prev : (prev[section] || {});
      const arr = [...(parent[arrKey] || []), blank];
      if (isRoot) return { ...prev, [arrKey]: arr };
      return { ...prev, [section]: { ...parent, [arrKey]: arr } };
    });
  };

  // Remove an item from an editable array. Asks for confirmation so a misclick
  // doesn't nuke a configured plan.
  const removeArrayItem = (section, arrKey, index) => {
    if (!confirm("Delete this item? You can re-add it later but the current copy will be lost.")) return;
    setResolved((prev) => {
      const isRoot = !section || section === "_root" || section === "_root2";
      const parent = isRoot ? prev : (prev[section] || {});
      const arr = [...(parent[arrKey] || [])];
      arr.splice(index, 1);
      if (isRoot) return { ...prev, [arrKey]: arr };
      return { ...prev, [section]: { ...parent, [arrKey]: arr } };
    });
  };

  // Reorder an item up/down inside an editable array.
  const moveArrayItem = (section, arrKey, index, delta) => {
    setResolved((prev) => {
      const isRoot = !section || section === "_root" || section === "_root2";
      const parent = isRoot ? prev : (prev[section] || {});
      const arr = [...(parent[arrKey] || [])];
      const j = index + delta;
      if (j < 0 || j >= arr.length) return prev;
      [arr[index], arr[j]] = [arr[j], arr[index]];
      if (isRoot) return { ...prev, [arrKey]: arr };
      return { ...prev, [section]: { ...parent, [arrKey]: arr } };
    });
  };


  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/site-content/${page}`, { content: resolved });
      toast.success(`${pageMeta.label} copy saved — live now`);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!confirm(`Revert ALL ${pageMeta.label} copy back to the bundled defaults? Your customisations will be lost.`)) return;
    setSaving(true);
    try {
      await api.post(`/admin/site-content/${page}/reset`);
      await load();
      toast.success("Reset to defaults");
    } catch {
      toast.error("Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const resetSection = (sectionKey) => {
    if (!defaults) return;
    setResolved((prev) => ({ ...prev, [sectionKey]: structuredClone(defaults[sectionKey]) }));
    toast.info(`Section "${sectionKey}" reset — remember to Save to publish.`);
  };

  const toggleSection = (k) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  if (!resolved) return <div className="text-rf-ink-soft text-sm py-6">Loading {pageMeta.label.toLowerCase()} content…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-home-content-panel">
      <div className="card-recipe">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-[200px]">
            <p className="overline text-rf-orange mb-1">Site copy</p>
            <h2 className="font-display text-2xl font-black text-rf-ink">Edit any public page</h2>
          </div>
          <div className="flex gap-2 flex-wrap">
            <a href={pageMeta.route} target="_blank" rel="noreferrer" className="btn-outline-ink !py-2 !px-4 text-sm inline-flex items-center gap-1.5" data-testid="home-content-preview-btn">
              <Eye size={16} weight="bold" /> Open {pageMeta.label}
            </a>
            <button onClick={resetAll} disabled={saving} className="btn-outline-ink !py-2 !px-4 text-sm inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="home-content-reset-all-btn">
              <ArrowCounterClockwise size={16} weight="bold" /> Reset all
            </button>
            <button onClick={save} disabled={saving} className="btn-amber !py-2 !px-4 text-sm inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="home-content-save-btn">
              <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>

        {/* Page selector */}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="site-content-page-selector">
          {PAGE_REGISTRY.map((p) => (
            <button
              key={p.key}
              onClick={() => setPage(p.key)}
              data-testid={`site-content-page-${p.key}`}
              className={`text-xs px-3 py-1.5 rounded-full font-bold transition-colors ${page === p.key ? "bg-rf-ink text-rf-cream" : "bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-amber/30"}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <p className="text-sm text-rf-ink-soft">
          Every visible string on <span className="font-mono">{pageMeta.route}</span> is editable below. Changes go live the moment you hit <b>Save</b> — no redeploy. Use the <span className="font-mono bg-rf-cream-alt px-1.5 py-0.5 rounded">{`{FREE}`}</span> token to interpolate the current free-recipe limit.
        </p>
        {page === "faq" && (
          <p className="text-sm text-rf-orange font-bold mt-2" data-testid="faq-manager-hint">
            💡 To add, edit, reorder or delete the actual FAQ questions & answers, use the dedicated <span className="font-mono bg-rf-amber/20 px-1.5 py-0.5 rounded">FAQS</span> tab in the Admin nav.
          </p>
        )}
      </div>

      {page === "home" && (
        <HomeLayoutCard
          resolved={resolved}
          labelFor={(k) => SECTIONS.find((x) => x.key === k)?.label || k}
          setOrder={(order) => setResolved((prev) => ({ ...prev, layout: { ...(prev.layout || {}), order } }))}
          setDate={(k, field, v) => updateField(k, field, v)}
        />
      )}

      {SECTIONS.map((sec, sidx) => {
        // _root sections write directly to top-level keys, so the "content" we feed to fields IS resolved itself
        const isRoot = sec.key === "_root" || sec.key === "_root2";
        const sectionContent = isRoot ? resolved : (resolved[sec.key] || {});
        const sectionKeyForUI = sec.key || `arr-${sec.arrayKey}` || `sec-${sidx}`;
        const sectionId = `${sectionKeyForUI}-${sidx}`;
        const isOpen = openSections.has(sectionId);
        return (
          <div key={sectionId} className="card-recipe" data-testid={`home-content-section-${sectionKeyForUI}`}>
            <button
              type="button"
              onClick={() => toggleSection(sectionId)}
              className="w-full flex items-center justify-between text-left"
              data-testid={`home-content-section-toggle-${sectionKeyForUI}`}
            >
              <div>
                <p className="overline text-rf-orange mb-0.5">Section</p>
                <h3 className="font-display font-extrabold text-xl text-rf-ink">{sec.label || sec.arrayLabel}</h3>
              </div>
              <span className="text-rf-ink-soft text-sm font-bold">{isOpen ? "▼" : "▶"}</span>
            </button>

            {page === "home" && sec.key !== "seo" && (
              <label className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-rf-ink cursor-pointer" data-testid={`home-section-visible-${sec.key}`}>
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-rf-orange"
                  checked={!sectionContent.hidden}
                  onChange={(e) => updateField(sec.key, "hidden", !e.target.checked)}
                  data-testid={`home-section-visible-toggle-${sec.key}`}
                />
                Show this section on the home page
                <span className={`chip ${sectionContent.hidden ? "!bg-rf-cream !text-rf-ink-soft" : "!bg-emerald-500/20 !text-emerald-800"}`}>{sectionContent.hidden ? "HIDDEN" : "SHOWN"}</span>
              </label>
            )}

            {isOpen && (
              <div className="mt-5 space-y-4">
                {(sec.fields || []).map((f) => f.type === "hero_photo" ? (
                  <HeroPhotoField key={f.k} label={f.label} />
                ) : (
                  <Field
                    key={f.k}
                    label={f.label}
                    value={sectionContent[f.k] ?? ""}
                    textarea={f.textarea}
                    helper={f.helper}
                    onChange={(v) => updateField(sec.key, f.k, v)}
                    testid={`home-content-${sectionKeyForUI}-${f.k}`}
                  />
                ))}

                {/* Live SEO preview — appears for the SEO section only.
                    Renders a modal with Google + WhatsApp + Facebook cards
                    from the current form values, updated per keystroke. */}
                {sec.key === "seo" && (
                  <div className="pt-2">
                    <SnippetPreview
                      title={sectionContent.seo_title || ""}
                      description={sectionContent.seo_description || ""}
                      url={`https://recipefiler.com${pageMeta.route || "/"}`}
                      brand="Recipe Filer"
                      dataTestidPrefix={`snippet-preview-${page}`}
                    />
                  </div>
                )}

                {sec.arrayKey && (
                  <div className={sec.fields ? "pt-4 mt-4 border-t border-rf-ink/10" : ""}>
                    {sec.fields && <p className="font-display font-extrabold text-rf-ink mb-3">{sec.arrayLabel}</p>}
                    {(() => {
                      // Some section arrays live at the top level of resolved
                      // (`_root` sections + top-level array-only sections like
                      // `input_paths` under how_to_use whose key is undefined),
                      // others live nested under `sec.key` (e.g. `three_ways.paths`,
                      // `how_it_works.steps`, `features.items`). Pick the right
                      // source AND pass the matching section arg to the update
                      // handlers so add/reorder/remove hit the right slot.
                      const nested = sec.key && !isRoot;
                      const sectionArg = nested ? sec.key : null;
                      const arrItems = nested
                        ? (resolved[sec.key]?.[sec.arrayKey] || [])
                        : (resolved[sec.arrayKey] || []);
                      return (
                        <>
                          {arrItems.map((item, idx, arr) => (
                            <div key={idx} className="bg-rf-cream-alt rounded-xl p-4 mb-3" data-testid={`home-content-${sec.arrayKey}-${idx}`}>
                              <div className="flex items-center justify-between mb-3 gap-2">
                                <p className="text-xs font-black uppercase tracking-wider text-rf-orange">Item {idx + 1}</p>
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => moveArrayItem(sectionArg, sec.arrayKey, idx, -1)}
                                    disabled={idx === 0}
                                    data-testid={`home-content-${sec.arrayKey}-${idx}-up`}
                                    title="Move up"
                                    className="px-2 py-1 rounded-md text-xs font-bold bg-white border border-rf-ink/15 text-rf-ink hover:bg-rf-amber/30 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >↑</button>
                                  <button
                                    type="button"
                                    onClick={() => moveArrayItem(sectionArg, sec.arrayKey, idx, 1)}
                                    disabled={idx === arr.length - 1}
                                    data-testid={`home-content-${sec.arrayKey}-${idx}-down`}
                                    title="Move down"
                                    className="px-2 py-1 rounded-md text-xs font-bold bg-white border border-rf-ink/15 text-rf-ink hover:bg-rf-amber/30 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >↓</button>
                                  <button
                                    type="button"
                                    onClick={() => removeArrayItem(sectionArg, sec.arrayKey, idx)}
                                    data-testid={`home-content-${sec.arrayKey}-${idx}-remove`}
                                    title="Delete item"
                                    className="px-2 py-1 rounded-md text-xs font-bold bg-white border border-red-300 text-red-700 hover:bg-red-50"
                                  >✕</button>
                                </div>
                              </div>
                              <div className="space-y-3">
                                {sec.arrayFields.map((f) => f.type === "icon" ? (
                                  <IconPicker
                                    key={f.k}
                                    label={f.label}
                                    value={item[f.k] || ""}
                                    onChange={(v) => updateArrayField(sectionArg, sec.arrayKey, idx, f.k, v)}
                                    testid={`home-content-${sec.arrayKey}-${idx}-icon`}
                                  />
                                ) : (
                                  <Field
                                    key={f.k}
                                    label={f.label}
                                    value={item[f.k] ?? ""}
                                    textarea={f.textarea}
                                    onChange={(v) => updateArrayField(sectionArg, sec.arrayKey, idx, f.k, v)}
                                    testid={`home-content-${sec.arrayKey}-${idx}-${f.k}`}
                                  />
                                ))}
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addArrayItem(sectionArg, sec.arrayKey, sec.arrayFields)}
                            data-testid={`home-content-${sec.arrayKey}-add`}
                            className="w-full mt-1 px-4 py-2.5 rounded-xl border-2 border-dashed border-rf-orange/50 text-rf-orange font-bold text-sm hover:bg-rf-amber/20 transition-colors"
                          >
                            + Add new {sec.arrayItemLabel || "item"}
                          </button>
                        </>
                      );
                    })()}
                  </div>
                )}

                <div className="pt-3 flex justify-end">
                  <button
                    onClick={() => resetSection(sec.key)}
                    className="text-xs text-rf-ink-soft hover:text-rf-orange inline-flex items-center gap-1"
                    data-testid={`home-content-section-reset-${sec.key}`}
                  >
                    <ArrowCounterClockwise size={12} weight="bold" /> Reset this section to defaults
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const HOME_KEYS = ["hero", "three_ways", "how_it_works", "features", "shopping_list", "meal_planner", "directory", "news", "cta"];

// Drag (or arrow) home sections into order and give each an optional show-from / until window.
function HomeLayoutCard({ resolved, labelFor, setOrder, setDate }) {
  const order = [...(resolved.layout?.order || []), ...HOME_KEYS].filter((k, i, a) => HOME_KEYS.includes(k) && a.indexOf(k) === i);
  const [dragKey, setDragKey] = useState(null);
  const move = (from, to) => {
    if (to < 0 || to >= order.length || from === to) return;
    const next = [...order];
    const [k] = next.splice(from, 1);
    next.splice(to, 0, k);
    setOrder(next);
  };
  const today = new Date().toLocaleDateString("en-CA"); // local YYYY-MM-DD
  const status = (c) => {
    if (c.hidden) return ["HIDDEN", "!bg-rf-cream !text-rf-ink-soft"];
    if (c.show_from && today < c.show_from) return ["SCHEDULED", "!bg-sky-500/20 !text-sky-800"];
    if (c.show_until && today > c.show_until) return ["ENDED", "!bg-rf-cream !text-rf-ink-soft"];
    return ["LIVE", "!bg-emerald-500/20 !text-emerald-800"];
  };
  return (
    <div className="card-recipe" data-testid="home-layout-card">
      <p className="overline text-rf-orange mb-1">Home page layout</p>
      <h3 className="font-display text-xl font-black text-rf-ink mb-1">Section order &amp; schedule</h3>
      <p className="text-sm text-rf-ink-soft mb-4">Drag sections (or use the arrows) to reorder them. Add dates to show a section only between them — e.g. Christmas baking from 1 to 25 December. Leave dates blank to always show. Remember to <b>Save</b>.</p>
      <ol className="space-y-2">
        {order.map((k, i) => {
          const c = resolved[k] || {};
          const [label, cls] = status(c);
          return (
            <li
              key={k}
              draggable
              onDragStart={() => setDragKey(k)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragKey) move(order.indexOf(dragKey), i); setDragKey(null); }}
              className={`flex flex-wrap items-center gap-3 p-3 rounded-xl border bg-white ${dragKey === k ? "opacity-50 border-rf-orange" : "border-rf-ink/10"}`}
              data-testid={`home-layout-row-${k}`}
            >
              <span className="cursor-grab text-rf-ink-soft select-none" title="Drag to reorder">⋮⋮</span>
              <span className="w-6 text-xs font-black text-rf-ink-soft">{i + 1}</span>
              <span className="font-bold text-rf-ink flex-1 min-w-[140px]">{labelFor(k)}</span>
              <span className={`chip ${cls}`} data-testid={`home-layout-status-${k}`}>{label}</span>
              <label className="text-xs text-rf-ink-soft flex items-center gap-1">From
                <input type="date" value={c.show_from || ""} onChange={(e) => setDate(k, "show_from", e.target.value)}
                  className="border border-rf-ink/15 rounded-lg px-2 py-1 text-xs" data-testid={`home-layout-from-${k}`} />
              </label>
              <label className="text-xs text-rf-ink-soft flex items-center gap-1">Until
                <input type="date" value={c.show_until || ""} onChange={(e) => setDate(k, "show_until", e.target.value)}
                  className="border border-rf-ink/15 rounded-lg px-2 py-1 text-xs" data-testid={`home-layout-until-${k}`} />
              </label>
              <div className="flex gap-1">
                <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} aria-label="Move up"
                  className="w-8 h-8 rounded-lg bg-rf-cream-alt font-bold disabled:opacity-30" data-testid={`home-layout-up-${k}`}>↑</button>
                <button type="button" onClick={() => move(i, i + 1)} disabled={i === order.length - 1} aria-label="Move down"
                  className="w-8 h-8 rounded-lg bg-rf-cream-alt font-bold disabled:opacity-30" data-testid={`home-layout-down-${k}`}>↓</button>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// Pick a box icon from the shared set; "Default" keeps the built-in icon for that position.
function IconPicker({ label, value, onChange, testid }) {
  const opts = [["", null], ...Object.entries(HOME_ICONS)];
  return (
    <div data-testid={testid}>
      <label className="block text-xs font-bold text-rf-ink-soft uppercase tracking-wide mb-1.5">{label}</label>
      <div className="flex flex-wrap gap-1.5">
        {opts.map(([k, o]) => (
          <button
            key={k || "default"}
            type="button"
            title={o ? o.label : "Default icon"}
            onClick={() => onChange(k)}
            data-testid={`${testid}-${k || "default"}`}
            className={`w-10 h-10 rounded-lg border inline-flex items-center justify-center transition-colors ${value === k ? "bg-rf-amber border-rf-orange text-rf-ink" : "bg-white border-rf-ink/15 text-rf-orange hover:bg-rf-amber/20"}`}
          >
            {o ? <o.Icon size={20} weight="duotone" /> : <span className="text-[9px] font-black uppercase text-rf-ink-soft">Auto</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

const HERO_FALLBACK = "https://images.unsplash.com/photo-1556909115-3f381036bcb3?w=1200&q=85";

// Hero photo lives in site settings (hero_image_url); upload saves instantly with a live home-style preview.
function HeroPhotoField({ label }) {
  const [url, setUrl] = useState(undefined);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setUrl(r.data?.hero_image_url || "")); }, []);
  const save = async (next) => {
    await api.patch("/settings", { hero_image_url: next || null });
    setUrl(next || "");
  };
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Please keep uploads under 10 MB."); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      await save(data.url);
      toast.success("Hero photo updated — live now");
    } catch {
      toast.error("Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="hero-photo-field">
      <label className="block text-xs font-bold text-rf-ink-soft uppercase tracking-wide mb-1.5">{label}</label>
      <div className="flex flex-wrap items-start gap-6">
        <div className="w-40 shrink-0 p-2">
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-xl rotate-2 bg-rf-cream-alt" data-testid="hero-photo-preview">
            {url === undefined
              ? <div className="w-full h-full flex items-center justify-center text-xs text-rf-ink-soft">Loading…</div>
              : <Media src={url || HERO_FALLBACK} alt="Hero preview" className="w-full h-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-t from-rf-ink/40 to-transparent" />
          </div>
        </div>
        <div className="flex-1 min-w-[200px] space-y-2">
          <p className="text-xs text-rf-ink-soft">Shown on the right of the home page top section. Portrait works best (about 1200×1500), JPG/WEBP under 1 MB — or a short looping MP4/WebM video. Saves instantly.</p>
          <label className={`btn-amber !py-2 !px-4 text-sm inline-flex cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}>
            {busy ? "Uploading…" : url ? "Swap photo" : "Upload photo"}
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime" className="hidden"
              data-testid="hero-photo-input" onChange={(e) => upload(e.target.files?.[0])} />
          </label>
          {url && (
            <button type="button" data-testid="hero-photo-clear" onClick={() => save("").then(() => toast.success("Hero photo removed"))}
              className="block text-xs text-rf-ink-soft hover:text-red-600 underline">Remove and use the built-in photo</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, textarea, testid, helper }) {
  return (
    <div>
      <label className="block text-xs font-bold text-rf-ink-soft uppercase tracking-wide mb-1.5">{label}</label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          rows={3}
          className="w-full px-3 py-2 rounded-lg border border-rf-ink/15 bg-white text-rf-ink text-sm focus:outline-none focus:border-rf-orange resize-y"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full px-3 py-2 rounded-lg border border-rf-ink/15 bg-white text-rf-ink text-sm focus:outline-none focus:border-rf-orange"
        />
      )}
      {helper && (
        <p className="mt-1.5 text-xs text-rf-ink-soft leading-snug italic">{helper}</p>
      )}
      {value != null && value.length > 0 && (label.match(/(≤60|145–160|145-160)/)) && (
        <p className="mt-1 text-[10px] font-bold text-rf-ink-soft" data-testid={`${testid}-counter`}>
          {value.length} chars
        </p>
      )}
    </div>
  );
}
